import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";

const EditDeliveryDialog = ({
  open,
  onClose,
  deliveryName,
  token,
  onSuccess,
}) => {
  const [menuItems, setMenuItems] = useState({});
  const [updatedPrices, setUpdatedPrices] = useState({});
  const [search, setSearch] = useState("");
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenu(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-menu-with-archive`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const allItems = [
          ...(data.menu?.menuItems || []),
          ...((data.menu?.menuArchive || []).map((archived) => ({
            ...archived,
            isArchived: true,
          })) || []),
        ];

        const grouped = {};
        allItems.forEach((item) => {
          const category = item.categoryName || "Archive";
          const delivery = item.deliveries?.find(
            (d) => d.deliveryName === deliveryName
          );
          const entry = {
            _id: item._id,
            menuName: item.menuName,
            basePrice: item.basePrice,
            deliveryPrice: delivery ? delivery.deliveryPrice : 0,
            isArchived: item.isArchived || false,
          };
          if (!grouped[category]) grouped[category] = [];
          grouped[category].push(entry);
        });

        setMenuItems(grouped);
      } catch (err) {
        console.error("Error fetching menu items:", err);
      } finally {
        setLoadingMenu(false);
      }
    };

    if (open) fetchMenuItems();
  }, [open, deliveryName, token]);

  const handleChange = (id, value) => {
    setUpdatedPrices((prev) => ({ ...prev, [id]: parseFloat(value) }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = Object.entries(updatedPrices).map(([id, price]) => ({
      menuItemId: id,
      deliveryName,
      deliveryPrice: price,
    }));

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/update-delivery-prices`,
        { updates: payload },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating delivery prices:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Delivery Prices for "{deliveryName}"</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search by name or category"
          value={search}
          onChange={(e) => setSearch(e.target.value.toLowerCase())}
          margin="dense"
          disabled={loadingMenu}
        />

        {loadingMenu ? (
          <Box
            sx={{ display: "flex", alignItems: "center", mt: 3, mb: 2, gap: 2 }}
          >
            <CircularProgress size={24} />
            <Typography>Fetching Menu...</Typography>
          </Box>
        ) : (
          Object.entries(menuItems)
            .map(([category, items]) => {
              const filteredItems = items.filter(
                (item) =>
                  item.menuName.toLowerCase().includes(search) ||
                  category.toLowerCase().includes(search)
              );
              return filteredItems.length > 0
                ? [category, filteredItems]
                : null;
            })
            .filter(Boolean)
            .map(([category, items]) => (
              <Accordion key={category} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {category}
                    {items.some((i) => i.isArchived) && " (Archived)"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Menu Name</TableCell>
                        <TableCell align="right">Base Price</TableCell>
                        <TableCell align="right">Delivery Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item._id}>
                          <TableCell>{item.menuName}</TableCell>
                          <TableCell align="right" sx={{ width: 120 }}>
                            ₱{(item.basePrice || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 120 }}>
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={
                                updatedPrices[item._id] !== undefined
                                  ? updatedPrices[item._id]
                                  : item.deliveryPrice
                              }
                              onChange={(e) =>
                                handleChange(item._id, e.target.value)
                              }
                              inputProps={{ min: 0 }}
                              disabled={submitting}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingMenu}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeliveryDialog;
